// library
import { Link, useLocation, useNavigate } from "react-router-dom";
// style
import useGroupAPIStore from "apis/admin/group.api";
import useRoleAPIStore from "apis/admin/role.api";
import { useDialogContext } from "components/dialog/controller/DialogProvider";
import { Icon } from "components/icon/Icon";
import {
  NAV_MENU,
  NavMenuProps,
  NavSubMenuProps,
  S_CORE_NAV_MENU,
} from "const/const";
import { UserModal } from "container/Admin/Users/Modal/UserModal";
import { LogoutDialog } from "container/Common/Dialog/LogoutDialog";
import { SwitchPagesDialog } from "container/Common/Dialog/SwitchPagesDialog";
import { Icons } from "contents/icons";
import { handleActionKeyPress } from "hooks";
import useUser from "hooks/useUser";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import useStepsStore from "store/project/data/steps/useStepsStore";
import useDashboardStore from "store/project/data/useDashboardStore";
import useTableDataStore from "store/project/data/useTableDataStore";
import useAccessStore from "store/useAccessStore";
import useAuthStore from "store/useAuthStore";
import useDarkModeStore from "store/useDarkModeStore";
import useLayoutStore from "store/useLayoutStore";
import useModalStore from "store/useModalStore";
import usePageStore from "store/usePageStore";
import useProjectDataStore from "store/useProjectDataStore";
import useToastStore from "store/useToastStore";
import { UserInfoDataType } from "store/useUserStore";
import { GroupNameType, RoleType } from "types/admin/AdminType";
import { DropDownListOptionType } from "types/common/CommonType";
import { Util } from "Util";
import SubTab from "../SubTab/SubTab";
import { StyledNavigationBar } from "./style";
import { DEV_MODE } from "config";
import useAuthAPIStore from "apis/auth/auth.api";
import useReportDataStore from "store/report/useReportDataStore";
import { FEATURES } from "../../../const/feature/const";
import { useTitleBar } from "hooks/useTitleBar";

function NavigationBar() {
  const isLogin = useAuthStore((state) => state.isLogin);
  const { setEditMode } = useModalStore();
  const [user, setUser] = useState<UserInfoDataType>();
  const [userModal, setUserModal] = useState(false);
  const [isMenuList, setIsMenuList] = useState(false);
  const menuListRef = useRef<HTMLDivElement>(null);

  const { getUserInfo, clear } = useUser();
  const locationPath = useLocation().pathname.split("/")[1];
  const fullLocation = useLocation().pathname;
  const { isDarkMode, setIsDarkMode } = useDarkModeStore();
  const { connectDatasetMode, projectData, isProjectInfoDashboard } =
    useProjectDataStore();
  const { isReportInfoDashboard, reportData } = useReportDataStore();
  const { getGroupNameList } = useGroupAPIStore();
  const { getRolesList } = useRoleAPIStore();
  const { gridSizeData } = useDashboardStore();
  const { isStepEditMode } = useStepsStore();
  const { isPage, setListDepthList } = usePageStore();
  const { isFullScreen, setIsFullScreen } = useTableDataStore();
  const { activeToast } = useToastStore();
  const { openDialog } = useDialogContext();
  const accessRole = useAccessStore((state) => state.accessRole);
  const { isTitleBar } = useTitleBar();

  const [groupDropdownOption, setGroupDropdownOption] = useState<
    DropDownListOptionType[]
  >([]);
  const [roleDropdownOption, setRoleDropdownOption] = useState<
    DropDownListOptionType[]
  >([]);

  const navigate = useNavigate();
  const { userLogout } = useAuthAPIStore();
  const { isSubTab } = useLayoutStore();
  const canAccessReport = DEV_MODE ?? false;

  /**
   * @description role에 따른 서브 메뉴 필터링 항목
   * @author jh_jeong
   */
  const subMenuAccessMap: Record<string, Record<string, boolean>> = {
    admin: {
      users: accessRole.users.viewUsers,
      groups: accessRole.groups.viewGroupDetails,
      roles: accessRole.roles.viewRoles,
      domains: accessRole.administration.domainSettings,
      userLogs: accessRole.administration.viewUserLogs,
      connectors: accessRole.connectors.readConnector,
    },
    library: {
      logs: accessRole.library.viewExportLog,
    },
  };

  // connector만 true이고 나머지가 모두 false인지 확인
  const shouldHideAdmin = Object.entries(subMenuAccessMap.admin)
    .filter(([key]) => key !== "connectors") // connector 제외
    .every(([_, value]) => value === false); // 나머지가 모두 false인지 확인

  /**
   * @description role에 따른 메인 메뉴 필터링 항목
   * @author jh_jeong
   */
  const mainMenuAccessMap: Record<string, boolean> = {
    flow: accessRole.projectFlows.viewFlows,
    report: canAccessReport && accessRole.report.readReport,
    admin: !shouldHideAdmin,
  };

  const filteredMainMenu: NavMenuProps[] = (
    FEATURES.S_CORE_MODE ? S_CORE_NAV_MENU : NAV_MENU
  )
    .map((menu) => {
      const filteredChildren =
        menu.children?.filter((child) => {
          const pageSpecificAccess = subMenuAccessMap[menu.key] || {};
          return pageSpecificAccess[child.key] ?? true; // 서브 메뉴 접근 권한
        }) || [];

      return {
        ...menu,
        children: filteredChildren,
      };
    })
    .filter((menu) => {
      return mainMenuAccessMap[menu.key] ?? true; // 메인 메뉴 접근 권한
    });

  const activeSubMenu: NavSubMenuProps[] = (
    filteredMainMenu.find((menu) => menu.key === isPage)?.children ?? []
  ).filter((menu) => {
    return menu.isSuperUser ? user?.isSuperuser : true;
  });

  const dropdownOptions: {
    group: DropDownListOptionType[];
    role: DropDownListOptionType[];
  } = {
    group: groupDropdownOption,
    role: roleDropdownOption,
  };

  const handleUserInfo = async () => {
    const userInfo = await getUserInfo();
    if (userInfo && !_.isEqual(userInfo, user)) {
      setUser(userInfo);
    }
  };

  const handleLogout = () => {
    openDialog(LogoutDialog, {
      buttonClick: async () => {
        try {
          const data = await userLogout();
          if (data.code === 200) {
            clear(); // 로그아웃 시 상태 초기화
            navigate("/login"); // 로그인 페이지로 이동
          } else {
            throw new Error("Logout failed");
          }
        } catch (error) {
          console.error("An error occurred during logout:", error);
          // 에러 토스트 메시지 표시
          activeToast({
            message: "An error occurred during logout.",
            type: "error",
            timeout: 5000,
          });
        }
      },
    });
  };

  const linkOnClickHandler = (event: any, path: string) => {
    // 프로젝트 수정 후 저장하지 않고 다른 페이지로 이동할 경우 경고창 띄워주기
    if (locationPath === "project" && projectData.projectId && isStepEditMode) {
      event.preventDefault();
      openDialog(SwitchPagesDialog, {
        buttonClick: () => {
          window.localStorage.removeItem("isRefresh");
          navigate(path);
        },
      });
    }
  };

  const handleFullScreenToggle = (value: boolean) => {
    setIsFullScreen(value);

    // const elem = document.documentElement;
    // if (elem.requestFullscreen) elem.requestFullscreen();

    if (value) {
      activeToast({
        message: "Press ESC to exit Full-Screen.",
        type: "info",
        timeout: 3000,
      });
    }
  };

  const handleModalToggle = async (state: boolean) => {
    const fetchGroupList = async () => {
      const { resultList }: { resultList: GroupNameType[]; code: number } =
        await getGroupNameList(false);

      if (resultList?.length > 0) {
        setGroupDropdownOption(
          resultList.map((group: GroupNameType) => {
            return {
              label: group.groupName,
              value: group.groupId,
            };
          })
        );
      } else {
        // TODO: 그룹네임데이터가 없을 경우
      }
    };

    const fetchRoleList = async () => {
      const rolesList = await getRolesList("", "");

      setRoleDropdownOption(
        rolesList
          ? rolesList.map((roles: RoleType) => {
              return {
                label: roles.rolesName,
                value: roles.rolesId,
              };
            })
          : []
      );
    };
    await fetchGroupList();
    await fetchRoleList();
    setUserModal(state);
    setEditMode(state);
  };

  useEffect(() => {
    handleUserInfo();
  }, [getUserInfo()]);

  useEffect(() => {
    Util.handleObjectClose(menuListRef, setIsMenuList);
    handleActionKeyPress("Escape", () => setIsFullScreen(false));
    const isDarkModeString = localStorage.getItem("isDarkMode");
    if (isDarkModeString) setIsDarkMode(isDarkModeString === "true");
  }, []);

  const handleMyinfoClick = () => {
    /** TODO: 페이지 이동 시 새로고침 발생 => Link로 변경 필요 - c_bae */
    // window.location.href = "/";
    handleModalToggle(true);
  };

  return (
    <StyledNavigationBar $isFullScreen={isFullScreen ? "active" : ""}>
      <div className="header-container">
        <div className="navi-wrap">
          <div className="navi">
            {filteredMainMenu.map(
              (menu: any, idx: number) =>
                (menu.key === "" || isLogin) && (
                  <div
                    className={`${locationPath === menu.key && "active"} tabs`}
                    key={menu.key}
                  >
                    <Link
                      key={idx}
                      to={
                        (menu.children?.length && menu.children[0].path) ||
                        menu.path
                      }
                      onClick={(event) => linkOnClickHandler(event, menu.path)}
                      className="menu"
                    >
                      {menu.icon}
                      {menu.title && <p>{menu.title}</p>}
                    </Link>
                  </div>
                )
            )}
          </div>
        </div>

        <div className="right-box">
          {isProjectInfoDashboard && !isTitleBar && (
            <div className="project-info-wrap">
              {projectData.projectName && (
                <p className="title">{projectData.projectName}</p>
              )}
              {!connectDatasetMode && (
                <div className="info">
                  <div className="columns">
                    <p>
                      Columns{" "}
                      <span>{gridSizeData.columns.toLocaleString()}</span>
                    </p>
                  </div>
                  <div className="rows">
                    <p>
                      Rows <span>{gridSizeData.rows.toLocaleString()}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          {isReportInfoDashboard && reportData && !isTitleBar && (
            <div className="project-info-wrap">
              <p className="title">{reportData.reportName}</p>
              <div className="info">
                <div className="columns">
                  <p>
                    Updated{" "}
                    <span>
                      {Util.formatDate(reportData.lastModifiedDate, true)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
          {isLogin && user?.isSuperuser && (
            <div className="super-user-flag">
              <Icons.Award className="super-user-icon" />
              <div>SuperUser</div>
            </div>
          )}

          {isLogin && (
            <div className="profile-wrap">
              <div
                className={`profile ${user?.isSuperuser ? "super-user" : ""}`}
                onClick={() => setIsMenuList(!isMenuList)}
                ref={menuListRef}
              >
                <p>{(user && user.loginId[0]) || ""}</p>
                <CSSTransition
                  in={isMenuList}
                  timeout={200}
                  classNames={"combo-box"}
                  unmountOnExit
                >
                  <ul className="menu-list">
                    <li className="list" onClick={() => handleLogout()}>
                      <p>Logout</p>
                      <Icon icon="LogOut04" size={16} stroke="--text-gray-7" />
                    </li>
                    <span className="rectangle" />

                    <li className="list" onClick={handleMyinfoClick}>
                      <p>Myinfo</p>
                      <Icon
                        icon="UserCircle"
                        size={16}
                        stroke="--text-gray-7"
                      />
                    </li>
                    {locationPath === "project" && projectData.projectId && (
                      <li
                        className="list"
                        onClick={() => handleFullScreenToggle(!isFullScreen)}
                      >
                        <p>Full-screen</p>
                        <Icon
                          icon="Maximize01"
                          size={16}
                          stroke="--text-gray-7"
                        />
                      </li>
                    )}

                    <li
                      className="theme list"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        localStorage.setItem("isDarkMode", String(!isDarkMode));
                        setIsDarkMode(!isDarkMode);
                      }}
                    >
                      <p>{isDarkMode ? "Dark" : "Light"}</p>
                      <div
                        className={`theme-btn ${isDarkMode ? "dark" : "light"}`}
                      >
                        <span
                          className={`background`}
                          style={
                            isDarkMode
                              ? { left: "calc(100% - 3rem)" }
                              : { left: "0.2rem" }
                          }
                        />
                        <div className={`sun icon ${!isDarkMode && "active"}`}>
                          <Icon
                            icon="Sun"
                            size={16}
                            stroke="--accent-mandarin"
                          />
                        </div>
                        <div className={`moon icon ${isDarkMode && "active"}`}>
                          <Icon icon="Moon02" size={16} stroke="--normal" />
                        </div>
                      </div>
                    </li>
                  </ul>
                </CSSTransition>
              </div>
            </div>
          )}
        </div>
      </div>
      {isSubTab && <SubTab tabMenu={activeSubMenu} />}
      <CSSTransition
        in={userModal}
        timeout={200}
        unmountOnExit
        classNames="modal"
      >
        {/* 본인 계정은 권한 상관없이 수정할 수 있어야 함 */}
        <UserModal
          canModifyUsersRole={true}
          modalToggle={handleModalToggle}
          dropdownOptions={dropdownOptions}
          userId={user?.userId}
        />
      </CSSTransition>
    </StyledNavigationBar>
  );
}

export default NavigationBar;
